import { ICountry } from 'country-state-city/dist/lib/interface';
import React, { useEffect, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { BillingDetails } from 'src/ReduxStorage/PaymentTransaction/types';
import { Payment } from 'src/ReduxStorage/UserSubscriptionPlans/dispatchers';
import { ModalDialog } from 'src/web/components/Modals/ModalDialog';
import { SelectOption } from 'src/web/components/Select/component';
import { alertDialog as alert } from 'src/web/components/Modals/AlertDialog';
import { Spinner } from 'src/web/components/Spinner/Spinner';
import { isPhoneNumberRequired } from 'src/ReduxStorage/PaymentTransaction/helpers';
import { PhoneInput } from 'src/web/components/PhoneInput/component';
import {
  AddressContent,
  AddressRow,
  InputBox,
  StyledSelect,
  StyledTextField,
  SubHeader,
  ModalContentWrapper,
  PolicyLabel,
} from './styles';

export const BillingInformationModal: React.FC<{
  onHide: () => void;
  fetchPaymentForCurrentSubscription: () => Promise<Payment>;
  updateBillingDetailsForCurrentSubscription: (
    billingDetails: BillingDetails
  ) => Promise<void>;
}> = ({
  onHide,
  fetchPaymentForCurrentSubscription,
  updateBillingDetailsForCurrentSubscription,
}) => {
  const [currency, setCurrency] = useState<string>();
  const [name, setName] = useState<string>();
  const [companyName, setCompanyName] = useState<string>();
  const [countryCode, setCountryCode] = useState<string>();
  const [region, setRegion] = useState<string>();
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [regionOptions, setRegionOptions] = useState<SelectOption[]>([]);
  const [addressLine, setAddressLine] = useState<string>();
  const [city, setCity] = useState<string>();
  const [postalCode, setPostalCode] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<ICountry[]>([]);

  useEffect(() => {
    (async (): Promise<void> => {
      setLoading(true);
      try {
        const { Country, State } = await import('country-state-city');
        setCountries(Country.getAllCountries());

        const result = await fetchPaymentForCurrentSubscription();
        if (result && result.billingDetails) {
          const { billingDetails } = result;
          setCurrency(result.currency);
          setName(billingDetails.name);
          setCompanyName(billingDetails.companyName);
          setCountryCode(billingDetails.countryCode);
          setRegion(billingDetails.region);
          setCity(billingDetails.city);
          setPostalCode(billingDetails.postalCode);
          setPhoneNumber(billingDetails.phoneNumber);
          if (
            billingDetails.addressLines &&
            billingDetails.addressLines.length > 0
          ) {
            setAddressLine(billingDetails.addressLines[0]);
          }
          if (billingDetails.countryCode) {
            setRegionOptions(
              State.getStatesOfCountry(billingDetails.countryCode).map(
                (st) => ({
                  value: st.name,
                  label: st.name,
                })
              )
            );
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const countryChange = async (value: string): Promise<void> => {
    const { State } = await import('country-state-city');
    setCountryCode(value);
    setRegion('');
    setRegionOptions(
      State.getStatesOfCountry(value).map((st) => ({
        value: st.name,
        label: st.name,
      }))
    );
  };

  const save = async (): Promise<void> => {
    if (saving || loading) {
      return;
    }

    if (isPhoneNumberRequired(currency)) {
      if (!phoneNumber) {
        alert({
          title: 'Invalid',
          message: (
            <>
              <p>Please enter your mobile phone number.</p>
              <PolicyLabel>
                We value your privacy. We will only contact you to provide trial
                support and platform services in accordance with our{' '}
                <a
                  href="https://solutions.openlearning.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
              </PolicyLabel>
            </>
          ),
        });
        return;
      }

      if (!isValidPhoneNumber(phoneNumber || '')) {
        alert({
          title: 'Invalid',
          message: 'The mobile phone number is not valid.',
        });
        return;
      }
    }

    setSaving(true);
    try {
      await updateBillingDetailsForCurrentSubscription({
        name,
        companyName,
        countryCode,
        region,
        addressLines: addressLine ? [addressLine] : [],
        city,
        postalCode,
        phoneNumber,
      });
    } catch {
      alert('Unexpected error, please try again');
    }
    setSaving(false);
    onHide();
  };

  return (
    <ModalDialog
      ModalDialogWrappingComp={ModalContentWrapper}
      center
      title="Update billing information"
      show
      onHide={onHide}
      actions={[
        {
          showSpinner: saving,
          label: 'Save',
          onClick: save,
          type: 'default',
        },
      ]}
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          <InputBox>
            <label htmlFor="billing-information-company-name">
              Company name
            </label>
            <StyledTextField
              id="billing-information-company-name"
              value={companyName}
              onValueChange={setCompanyName}
            />
          </InputBox>

          <InputBox>
            <label htmlFor="billing-information-contact-name">
              Billing contact name
            </label>
            <StyledTextField
              id="billing-information-contact-name"
              value={name}
              onValueChange={setName}
            />
          </InputBox>
          {isPhoneNumberRequired(currency) && (
            <InputBox>
              <label htmlFor="billing-information-company-name">
                Phone number
              </label>
              <div>
                <PhoneInput
                  defaultCountry={
                    currency === 'MYR' ? 'MY' : currency === 'IDR' ? 'ID' : null
                  }
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                />
                <PolicyLabel>
                  We value your privacy. We will only contact you to provide
                  trial support and platform services in accordance with our{' '}
                  <a
                    href="https://solutions.openlearning.com/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                </PolicyLabel>
              </div>
            </InputBox>
          )}
          <hr />
          <SubHeader>Billing address</SubHeader>
          <AddressContent>
            <AddressRow>
              <InputBox>
                <StyledSelect
                  label="Country"
                  value={countryCode}
                  options={[
                    { value: '', label: 'Country' },
                    ...countries.map((option) => ({
                      value: option.isoCode,
                      label: option.name,
                    })),
                  ]}
                  onValueChange={countryChange}
                />
              </InputBox>
              <InputBox>
                <StyledSelect
                  label="State / Province"
                  value={region}
                  options={[
                    { value: '', label: 'State / Province' },
                    ...regionOptions,
                  ]}
                  onValueChange={setRegion}
                />
              </InputBox>
            </AddressRow>
            <AddressRow>
              <InputBox>
                <label htmlFor="billing-information-address-line">
                  Address line
                </label>
                <StyledTextField
                  id="billing-information-address-line"
                  value={addressLine}
                  onValueChange={setAddressLine}
                />
              </InputBox>
            </AddressRow>
            <AddressRow>
              <InputBox>
                <label htmlFor="billing-information-region">
                  City / district
                </label>
                <StyledTextField
                  id="billing-information-region"
                  value={city}
                  onValueChange={setCity}
                />
              </InputBox>
              <InputBox>
                <label htmlFor="billing-information-post-code">Post code</label>
                <StyledTextField
                  id="billing-information-post-code"
                  value={postalCode}
                  onValueChange={setPostalCode}
                />
              </InputBox>
            </AddressRow>
          </AddressContent>
        </>
      )}
    </ModalDialog>
  );
};
